import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Container,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  TableProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FiEdit2, FiSearch, FiTrash2 } from "react-icons/fi";
import { IoArrowDown } from "react-icons/io5";
import {
  FactoryDigitalmallBackofficeV1Retailer,
  RetailerServiceDeleteRetailerApiArg,
  useRetailerServiceDeleteRetailerMutation,
} from "../../app/genericApiGenerated";
import { useRef, useState } from "react";
import UpdateRetailerForm from "../../gen/Retailers/UpdateRetailerForm";
import Form from "@rjsf/core";
import { RJSFSchema } from "@rjsf/utils";

type RetailerData = {
  dataTable?: TableProps;
  retailers: FactoryDigitalmallBackofficeV1Retailer[];
};

const ModalDelete = ({
  isOpen,
  onClose,
  overlayStyle,
  initialRef,
  finalRef,
  deleteRetailer,
  selectedRetailerId,
}: any) => (
  <Modal isCentered isOpen={isOpen} onClose={onClose}>
    {overlayStyle}
    <ModalContent>
      <ModalHeader>Delete Retailer</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text>Are sure to delete this retailer!</Text>
      </ModalBody>
      <ModalFooter>
        <Button
          colorScheme="red"
          mr={3}
          onClick={() => {
            if (selectedRetailerId) {
              deleteRetailer(selectedRetailerId.split("/")[1]);
              onClose();
            }
          }}
        >
          Delete
        </Button>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

const ModalUpdate = ({
  isOpen,
  onClose,
  initialRef,
  finalRef,
  selectedRetailerId,
  updateRetailerFormRef,
}: any) => (
  <Modal
    initialFocusRef={initialRef}
    finalFocusRef={finalRef}
    isOpen={isOpen}
    onClose={onClose}
  >
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>Update Retailer</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6}>
        <UpdateRetailerForm
          selectedRetailer={selectedRetailerId.split("/")[1] || ""}
          onFormSubmitted={() => onClose()}
          isOpen={isOpen}
          ref={updateRetailerFormRef}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          colorScheme="green"
          mr={3}
          onClick={() => {
            updateRetailerFormRef.current?.submit();
          }}
        >
          Update
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

export function RetailersTable(props: RetailerData) {
  const updateRetailerFormRef = useRef<Form<any, RJSFSchema, any>>(null);
  const [selectedRetailerId, setSelectedRetailerId] = useState<string>("");
  const [
    retailerServiceDeleteRetailer, // This is the mutation trigger
  ] = useRetailerServiceDeleteRetailerMutation();

  function deleteRetailer(retailerId: string): any {
    const retailerArg: RetailerServiceDeleteRetailerApiArg = {
      retailer: retailerId,
    };

    retailerServiceDeleteRetailer(retailerArg)
      .unwrap()
      .then(() => {
        console.log("item deleted with id: ", retailerId);
      });
  }
  const {
    isOpen: modalUpdateIsOpen,
    onOpen: modalUpdateOnOpen,
    onClose: modalUpdateOnClose,
  } = useDisclosure();
  const {
    isOpen: modalDeleteIsOpen,
    onOpen: modalDeleteOnOpen,
    onClose: modalDeleteOnClose,
  } = useDisclosure();
  const initialUpdateRef = useRef(null);
  const initialDeleteRef = useRef(null);
  const finalUpdateRef = useRef(null);
  const finalDeleteRef = useRef(null);
  const OverlayStyle = () => (
    <ModalOverlay
      bg="blackAlpha.300"
      backdropFilter="blur(10px) hue-rotate(90deg)"
    />
  );
  const [overlay, setOverlay] = useState(<OverlayStyle />);
  const isMobile = useBreakpointValue({ base: true, md: false });
  return (
    <div>
      <Container maxWidth="100%" bg={useColorModeValue("gray.100", "gray.800")}>
        <Box bg="bg.surface" m="0" p="0">
          <Box
            bg="white"
            boxShadow={{ base: "none", md: "sm" }}
            borderRadius={{ base: "none", md: "lg" }}
          >
            <Stack spacing="5">
              <Box px={{ base: "4", md: "6" }} pt="5">
                <Stack
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Text textStyle="lg" fontWeight="medium">
                    Retailers
                  </Text>
                  <InputGroup maxW="xs">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="fg.muted" boxSize="5" />
                    </InputLeftElement>
                    <Input placeholder="Search" />
                  </InputGroup>
                </Stack>
              </Box>
              <Box overflowX="auto">
                <Table {...props.dataTable}>
                  <Thead>
                    <Tr>
                      <Th>
                        <HStack spacing="3">
                          <Checkbox />
                          <HStack spacing="1">
                            <Text>RetailerNameName</Text>
                            <Icon
                              as={IoArrowDown}
                              color="fg.muted"
                              boxSize="4"
                            />
                          </HStack>
                        </HStack>
                      </Th>
                      <Th>Description</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {props.retailers?.map((retailer) => (
                      <Tr key={retailer.name}>
                        <Td>
                          <HStack spacing="3">
                            <Checkbox />
                            <Box>
                              <Text fontSize="medium" fontWeight="medium">
                                {retailer.display_name}
                              </Text>
                            </Box>
                          </HStack>
                        </Td>
                        <Td fontSize="small" fontWeight="bold">
                          <Text color="fg.muted">{retailer.description}</Text>
                        </Td>
                        <Td>
                          <HStack spacing="1">
                            <IconButton
                              icon={<FiTrash2 />}
                              variant="tertiary"
                              aria-label="Delete retailer"
                              onClick={() => {
                                setSelectedRetailerId(retailer.name as string); // Assuming 'id' is the identifier for a retailer
                                modalDeleteOnOpen();
                              }}
                            />
                            <IconButton
                              icon={<FiEdit2 />}
                              variant="tertiary"
                              aria-label="Update retailer"
                              onClick={() => {
                                setSelectedRetailerId(retailer.name as string); // Assuming 'id' is the identifier for a retailer
                                modalUpdateOnOpen();
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              <Box px={{ base: "4", md: "6" }} pb="5">
                <HStack spacing="3" justify="space-between">
                  {!isMobile && (
                    <Text color="fg.muted" textStyle="sm">
                      Showing 1 to 5 of 42 results
                    </Text>
                  )}
                  <ButtonGroup
                    spacing="3"
                    justifyContent="space-between"
                    width={{ base: "full", md: "auto" }}
                    variant="secondary"
                  >
                    <Button color="blackAlpha.900">Previous</Button>
                    <Button color="blackAlpha.900">Next</Button>
                  </ButtonGroup>
                </HStack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Container>
      <ModalDelete
        isOpen={modalDeleteIsOpen}
        onClose={() => {
          modalDeleteOnClose();
          setSelectedRetailerId(""); // Reset the selectedRetailerId when the modal is closed
        }}
        initialRef={initialDeleteRef}
        finalRef={finalDeleteRef}
        overlayStyle={overlay}
        deleteRetailer={deleteRetailer}
        selectedRetailerId={selectedRetailerId}
      />
      <ModalUpdate
        isOpen={modalUpdateIsOpen}
        onClose={() => {
          modalUpdateOnClose();
          setSelectedRetailerId(""); // Reset the selectedRetailerId when the modal is closed
        }}
        initialRef={initialUpdateRef}
        finalRef={finalUpdateRef}
        updateRetailerFormRef={updateRetailerFormRef}
        selectedRetailerId={selectedRetailerId}
      />
    </div>
  );
}
